import React from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
} from '@patternfly/react-core';
import { Table, Thead, Tbody, Tr, Th, Td } from '@patternfly/react-table';
import { ExclamationCircleIcon, NetworkIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useVmNetwork } from '../../../hooks/useVmNetwork';
import { VmNetworkInterface } from '../../../types/models/VmNetwork';

interface VmNetworkTabProps {
  vmId: number;
}

const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--pf-t--global--font--family--mono)',
};

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 'small',
  color: 'var(--pf-t--global--text--color--subtle)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '8px',
  fontWeight: 600,
};

const ipChipStyle: React.CSSProperties = {
  background: 'var(--pf-t--global--color--fill--secondary)',
  padding: '2px 8px',
  borderRadius: '4px',
  fontFamily: 'var(--pf-t--global--font--family--mono)',
  fontSize: 'small',
  display: 'inline-block',
  margin: '2px 4px 2px 0',
};

function SingleInterfaceCard({ iface }: { iface: VmNetworkInterface }) {
  const hasIpv4 = iface.ipv4_addresses.length > 0;
  const hasIpv6 = iface.ipv6_addresses.length > 0;
  const hasAnyIp = hasIpv4 || hasIpv6;
  const hasBothFamilies = hasIpv4 && hasIpv6;

  return (
    <Card>
      <CardTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold' }}>{iface.name}</span>
          <Label color={iface.state === 'UP' ? 'green' : 'red'}>{iface.state}</Label>
        </div>
      </CardTitle>
      <CardBody style={{ padding: 0 }}>
        {/* Meta strip */}
        <div
          style={{
            background: 'var(--pf-t--global--color--fill--secondary)',
            padding: '8px 16px',
            display: 'flex',
            gap: '24px',
          }}
        >
          <div>
            <span style={{ color: 'var(--pf-t--global--text--color--subtle)', marginRight: '8px' }}>MAC</span>
            <span style={monoStyle}>{iface.mac_address || '\u2014'}</span>
          </div>
          <div>
            <span style={{ color: 'var(--pf-t--global--text--color--subtle)', marginRight: '8px' }}>MTU</span>
            <span style={monoStyle}>{iface.mtu !== null ? iface.mtu : '\u2014'}</span>
          </div>
        </div>

        {/* IP address grid */}
        {hasAnyIp ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: hasBothFamilies ? '1fr 1fr' : '1fr',
              padding: '16px',
            }}
          >
            {hasIpv4 && (
              <div style={hasBothFamilies ? { paddingRight: '16px' } : {}}>
                <div style={sectionHeaderStyle}>IPv4 Addresses</div>
                {iface.ipv4_addresses.map((addr) => (
                  <div
                    key={`${addr.address}/${addr.prefix_length}`}
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                  >
                    <span style={monoStyle}>
                      {addr.address}/{addr.prefix_length}
                    </span>
                    <span style={{ color: 'var(--pf-t--global--text--color--subtle)', ...monoStyle }}>
                      /{addr.prefix_length}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {hasIpv6 && (
              <div
                style={
                  hasBothFamilies
                    ? {
                        paddingLeft: '16px',
                        borderLeft: '1px solid var(--pf-t--global--border--color--default)',
                      }
                    : {}
                }
              >
                <div style={sectionHeaderStyle}>IPv6 Addresses</div>
                {iface.ipv6_addresses.map((addr) => (
                  <div
                    key={`${addr.address}/${addr.prefix_length}`}
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                  >
                    <span style={monoStyle}>
                      {addr.address}/{addr.prefix_length}
                    </span>
                    <span style={{ color: 'var(--pf-t--global--text--color--subtle)', ...monoStyle }}>
                      /{addr.prefix_length}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: '16px',
              textAlign: 'center',
              fontStyle: 'italic',
              color: 'var(--pf-t--global--text--color--subtle)',
            }}
          >
            No IP addresses assigned
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function IpChips({ addresses }: { addresses: { address: string; prefix_length: number }[] }) {
  if (addresses.length === 0) {
    return <span style={{ fontStyle: 'italic', color: 'var(--pf-t--global--text--color--subtle)' }}>None</span>;
  }
  return (
    <>
      {addresses.map((addr) => (
        <span key={`${addr.address}/${addr.prefix_length}`} style={ipChipStyle}>
          {addr.address}/{addr.prefix_length}
        </span>
      ))}
    </>
  );
}

function MultiInterfaceTable({ interfaces }: { interfaces: VmNetworkInterface[] }) {
  return (
    <Card>
      <CardTitle>Network interfaces</CardTitle>
      <CardBody>
        <Table aria-label="Network interfaces" variant="compact">
          <Thead>
            <Tr>
              <Th>Interface</Th>
              <Th>State</Th>
              <Th>MAC address</Th>
              <Th>MTU</Th>
              <Th>IPv4 addresses</Th>
              <Th>IPv6 addresses</Th>
            </Tr>
          </Thead>
          <Tbody>
            {interfaces.map((iface) => (
              <Tr key={iface.name}>
                <Td dataLabel="Interface" style={{ fontWeight: 'bold', verticalAlign: 'top' }}>
                  {iface.name}
                </Td>
                <Td dataLabel="State" style={{ verticalAlign: 'top' }}>
                  <Label color={iface.state === 'UP' ? 'green' : 'red'}>{iface.state}</Label>
                </Td>
                <Td dataLabel="MAC address" style={{ ...monoStyle, verticalAlign: 'top' }}>
                  {iface.mac_address || '\u2014'}
                </Td>
                <Td dataLabel="MTU" style={{ ...monoStyle, verticalAlign: 'top' }}>
                  {iface.mtu !== null ? iface.mtu : '\u2014'}
                </Td>
                <Td dataLabel="IPv4 addresses" style={{ verticalAlign: 'top' }}>
                  <IpChips addresses={iface.ipv4_addresses} />
                </Td>
                <Td dataLabel="IPv6 addresses" style={{ verticalAlign: 'top' }}>
                  <IpChips addresses={iface.ipv6_addresses} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </CardBody>
    </Card>
  );
}

const VmNetworkTab: React.FunctionComponent<VmNetworkTabProps> = ({ vmId }) => {
  const { data, loading, error, refetch } = useVmNetwork(vmId);

  if (loading) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <CenteredSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <EmptyState
          titleText="Unable to load network information"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error.message}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  const interfaces = data?.network.interfaces ?? [];

  if (interfaces.length === 0) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <EmptyState titleText="No network interfaces" icon={NetworkIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            No network interfaces have been reported for this virtual machine. The Manager Agent may not have reported
            yet.
          </EmptyStateBody>
        </EmptyState>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      {interfaces.length === 1 ? (
        <SingleInterfaceCard iface={interfaces[0]} />
      ) : (
        <MultiInterfaceTable interfaces={interfaces} />
      )}
    </div>
  );
};

export default VmNetworkTab;
